import React from 'react';
import { NavLink as Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';

const sections = [
  {
    id: 1,
    title: "Onze missie",
    content: (
      <div className="space-y-4">
        <p className="text-base font-medium text-foreground/90 leading-relaxed border-l-4 border-primary pl-4 italic">
          "Iedere huiseigenaar in Nederland verdient eerlijk, onafhankelijk en begrijpelijk advies over hetverduurzamen van zijn of haar woning — zonder verborgen belangen."
        </p>
        <p>
          Dat is de kern van alles wat wij doen bij Verduurzaamwijzer.nu. De markt voor woningverduurzaming is
          complex, onoverzichtelijk en soms ronduit misleidend. Verkopers met commissieprikkels, onduidelijke
          subsidieregels en technisch jargon maken het voor de gemiddelde huiseigenaar moeilijk om een goede
          beslissing te nemen.
        </p>
        <p>
          Wij zijn er om dat te veranderen.
        </p>
      </div>
    ),
  },
  {
    id: 2,
    title: "Waarom dit nodig is",
    content: (
      <div className="space-y-4">
        <p>
          Nederland staat voor een enorme verduurzamingsopgave. Meer dan 7 miljoen woningen moeten de komende
          decennia worden verduurzaamd om de klimaatdoelen te halen. Dat vraagt om grote investeringen van
          huiseigenaren — investeringen die alleen renderen als de juiste keuzes worden gemaakt.
        </p>
        <p>
          Toch ontbreekt het aan betrouwbare, onafhankelijke informatie. Veel platforms worden betaald door de
          aanbieders die zij aanbevelen. Wij niet. Onze inkomsten komen uitsluitend uit transparante
          samenwerkingen die onze redactionele onafhankelijkheid niet beïnvloeden.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          <strong>Wist u dat?</strong> Gemiddeld betaalt een huiseigenaar €15.000–€40.000 voor een complete
          verduurzaming. Een verkeerde keuze kan duizenden euro's kosten. Onafhankelijk advies is daarom geen
          luxe, maar een noodzaak.
        </div>
      </div>
    ),
  },
  {
    id: 3,
    title: "Onze kernwaarden",
    content: (
      <div className="space-y-3">
        <ul className="space-y-4 list-none">
          {[
            ["Onafhankelijkheid", "Wij laten ons advies niet beïnvloeden door commerciële belangen. Aanbieders kunnen zich niet inkopen in onze aanbevelingen."],
            ["Transparantie", "Wij zijn open over hoe wij werken, hoe wij geld verdienen en welke criteria wij hanteren bij het beoordelen van aanbieders."],
            ["Betrouwbaarheid", "Alle informatie op ons platform wordt geschreven en gecontroleerd door vakspecialisten en regelmatig bijgewerkt."],
            ["Toegankelijkheid", "Wij maken complexe materie begrijpelijk voor iedereen, ongeacht technische kennis of achtergrond."],
            ["Duurzaamheid", "Wij geloven in een duurzame toekomst en dragen daar actief aan bij door huiseigenaren te helpen de juiste stappen te zetten."],
          ]?.map(([label, desc], i) => (
            <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
              <span className="mt-1 w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              <span><strong>{label}:</strong> {desc}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: 4,
    title: "Onze doelstelling",
    content: (
      <div className="space-y-4">
        <p>
          Wij streven ernaar om in 2030 de meest vertrouwde bron van informatie over woningverduurzaming in
          Nederland te zijn. Niet de grootste, maar de meest betrouwbare.
        </p>
        <p>Concreet betekent dat:</p>
        <ul className="space-y-2 list-none">
          {[
            "Minimaal 500.000 huiseigenaren per jaar voorzien van onafhankelijk advies.",
            "Een netwerk van uitsluitend gecertificeerde en beoordeelde aanbieders.",
            "Actuele informatie over alle relevante subsidies en regelingen.",
            "Volledige transparantie over onze werkwijze en financiering.",
          ]?.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: 5,
    title: "Hoe wij bijdragen",
    content: (
      <div className="space-y-4">
        <p>
          Elke dag werken wij aan het verbeteren van ons platform, het uitbreiden van onze kennisbank en het
          screenen van nieuwe aanbieders. Wij volgen actief de ontwikkelingen in wet- en regelgeving, technologie
          en de energiemarkt om u altijd de meest actuele informatie te bieden.
        </p>
        <p>
          Wilt u meer weten over hoe wij concreet te werk gaan? Lees dan onze{' '}
          <Link to="/werkwijze" className="text-primary hover:underline font-medium">Werkwijze-pagina</Link>{' '}
          of bekijk hoe wij onze{' '}
          <Link to="/onafhankelijkheid" className="text-primary hover:underline font-medium">onafhankelijkheid waarborgen</Link>.
        </p>
      </div>
    ),
  },
];

const OnzeMissiePage = () => {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/8 via-background to-background pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 text-sm text-foreground/50 mb-4">
              <Link to="/homepage" className="hover:text-primary transition-colors">Home</Link>
              <Icon name="ChevronRight" size={14} />
              <Link to="/over-ons" className="hover:text-primary transition-colors">Over Ons</Link>
              <Icon name="ChevronRight" size={14} />
              <span className="text-foreground/70">Onze Missie</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="Target" size={20} className="text-primary" />
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">Onze Missie</h1>
            </div>
            <p className="text-foreground/70 text-lg leading-relaxed max-w-2xl">
              Waarom wij bestaan, wat ons drijft en hoe wij bijdragen aan een duurzamer Nederland.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10">
            <aside className="lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3">Inhoud</h2>
                <nav className="space-y-1">
                  {sections?.map((s) => (
                    <a
                      key={s?.id}
                      href={`#section-${s?.id}`}
                      className="block text-sm text-foreground/60 hover:text-primary transition-colors py-0.5 leading-snug"
                    >
                      {s?.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            <main className="flex-1 min-w-0">
              <div className="space-y-10">
                {sections?.map((section, index) => (
                  <motion.div
                    key={section?.id}
                    id={`section-${section?.id}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.4, delay: index * 0.03 }}
                    className="scroll-mt-24"
                  >
                    <h2 className="font-heading text-lg font-semibold text-foreground mb-3 pb-2 border-b border-border/50">
                      {section?.title}
                    </h2>
                    <div className="text-foreground/75 leading-relaxed text-sm sm:text-base">
                      {section?.content}
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="mt-12 bg-primary/5 border border-primary/20 rounded-xl p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Leaf" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">Samen verduurzamen</h3>
                    <p className="text-sm text-foreground/70 mb-3">
                      Wilt u bijdragen aan onze missie of heeft u vragen? Neem contact op via{' '}
                      <a href="mailto:contact@verduurzaamwijzer.nu" className="text-primary hover:underline font-medium">
                        contact@verduurzaamwijzer.nu
                      </a>.
                    </p>
                    <p className="text-xs text-foreground/50">
                      De Content Factory BV · Oude Middenweg 17, 2491 AC Den Haag · KvK 84512490
                    </p>
                  </div>
                </div>
              </motion.div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OnzeMissiePage;
